import React, { useState, useEffect, useRef, useContext, createContext, useCallback } from 'react';
import { Phone, PhoneOff, Video, VideoOff, Mic, MicOff, PhoneIncoming, Volume2, VolumeX, Monitor, Pause, Play, FileText, X } from 'lucide-react';
import { Button } from '../../components/ui/button';
import { toast } from 'sonner';
import axios from 'axios';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

// ICE servers for WebRTC - using multiple STUN servers for reliability
const ICE_SERVERS = {
  iceServers: [
    { urls: 'stun:stun.l.google.com:19302' },
    { urls: 'stun:stun1.l.google.com:19302' },
    { urls: 'stun:stun2.l.google.com:19302' },
    { urls: 'stun:stun3.l.google.com:19302' },
    { urls: 'stun:stun4.l.google.com:19302' },
  ],
  iceCandidatePoolSize: 10
};

// Ringtone URLs
const RINGTONE_INCOMING = 'https://assets.mixkit.co/active_storage/sfx/2354/2354-preview.mp3';
const RINGTONE_OUTGOING = 'https://assets.mixkit.co/active_storage/sfx/1361/1361-preview.mp3';
const SOUND_CALL_END = 'https://assets.mixkit.co/active_storage/sfx/2572/2572-preview.mp3';

// Call Provider Context
export const CallContext = createContext(null);

export const useCall = () => {
  const context = useContext(CallContext);
  if (!context) {
    console.warn('useCall must be used within a CallProvider');
  }
  return context;
};

export const CallProvider = ({ children, currentUser, getAuthHeaders }) => {
  // State
  const [incomingCall, setIncomingCall] = useState(null);
  const [activeCall, setActiveCall] = useState(null);
  const [callStatus, setCallStatus] = useState('idle');
  const [isMuted, setIsMuted] = useState(false);
  const [isVideoOff, setIsVideoOff] = useState(false);
  const [isSpeakerOff, setIsSpeakerOff] = useState(false);
  const [callDuration, setCallDuration] = useState(0);
  const [wsConnected, setWsConnected] = useState(false);
  
  // New features
  const [isScreenSharing, setIsScreenSharing] = useState(false);
  const [isOnHold, setIsOnHold] = useState(false);
  const [callNotes, setCallNotes] = useState('');
  const [showCallNotes, setShowCallNotes] = useState(false);
  const [callHistory, setCallHistory] = useState([]);

  // Refs
  const wsRef = useRef(null);
  const peerConnectionRef = useRef(null);
  const localStreamRef = useRef(null);
  const localVideoRef = useRef(null);
  const remoteVideoRef = useRef(null);
  const remoteAudioRef = useRef(null);
  const incomingRingtoneRef = useRef(null);
  const outgoingRingtoneRef = useRef(null);
  const callTimerRef = useRef(null);
  const callTimeoutRef = useRef(null);
  const pendingIceCandidatesRef = useRef([]);
  // Use refs to track current call state for callbacks
  const activeCallRef = useRef(null);
  const incomingCallRef = useRef(null);
  
  // Keep refs in sync with state
  useEffect(() => {
    activeCallRef.current = activeCall;
  }, [activeCall]);
  
  useEffect(() => {
    incomingCallRef.current = incomingCall;
  }, [incomingCall]);

  // WebSocket connection
  useEffect(() => {
    if (!currentUser?.username) return;

    const connectWebSocket = () => {
      const wsUrl = BACKEND_URL.replace('https://', 'wss://').replace('http://', 'ws://');
      console.log('[Call] Connecting WebSocket for:', currentUser.username);
      
      const ws = new WebSocket(`${wsUrl}/api/calling/ws/${currentUser.username}`);
      
      ws.onopen = () => {
        console.log('[Call] WebSocket connected');
        setWsConnected(true);
        
        // Ping every 25 seconds
        ws.pingInterval = setInterval(() => {
          if (ws.readyState === WebSocket.OPEN) {
            ws.send(JSON.stringify({ type: 'ping' }));
          }
        }, 25000);
      };

      ws.onmessage = async (event) => {
        try {
          const data = JSON.parse(event.data);
          console.log('[Call] WS message received:', data.type, data);
          
          switch (data.type) {
            case 'incoming_call':
              handleIncomingCall(data.data);
              break;
            case 'call_accepted':
              handleCallAccepted(data);
              break;
            case 'call_rejected':
              handleCallRejected();
              break;
            case 'call_busy':
              handleCallBusy();
              break;
            case 'call_ended':
              handleCallEnded(data);
              break;
            case 'offer':
              await handleOffer(data);
              break;
            case 'answer':
              await handleAnswer(data);
              break;
            case 'ice-candidate':
              await handleIceCandidate(data);
              break;
            case 'pong':
              // Ping response, ignore
              break;
            default:
              console.log('[Call] Unknown message type:', data.type);
          }
        } catch (e) {
          console.error('[Call] Error processing message:', e);
        }
      };

      ws.onerror = (error) => {
        console.error('[Call] WebSocket error:', error);
        setWsConnected(false);
      };

      ws.onclose = () => {
        console.log('[Call] WebSocket disconnected');
        setWsConnected(false);
        if (ws.pingInterval) clearInterval(ws.pingInterval);
        
        // Reconnect after 3 seconds
        setTimeout(() => {
          if (wsRef.current === ws) {
            connectWebSocket();
          }
        }, 3000);
      };

      wsRef.current = ws;
    };

    connectWebSocket();

    return () => {
      if (wsRef.current) {
        if (wsRef.current.pingInterval) clearInterval(wsRef.current.pingInterval);
        wsRef.current.close();
        wsRef.current = null;
      }
    };
  }, [currentUser?.username]);

  // Call duration timer
  useEffect(() => {
    if (callStatus === 'connected') {
      setCallDuration(0);
      callTimerRef.current = setInterval(() => {
        setCallDuration(prev => prev + 1);
      }, 1000);
    } else {
      if (callTimerRef.current) {
        clearInterval(callTimerRef.current);
        callTimerRef.current = null;
      }
    }
    
    return () => {
      if (callTimerRef.current) {
        clearInterval(callTimerRef.current);
      }
    };
  }, [callStatus]);

  // Ringtone helpers
  const playRingtone = (type) => {
    stopAllRingtones();
    try {
      if (type === 'incoming' && incomingRingtoneRef.current) {
        incomingRingtoneRef.current.currentTime = 0;
        incomingRingtoneRef.current.volume = 0.5;
        incomingRingtoneRef.current.play().catch(e => console.log('[Call] Ringtone play error:', e));
      } else if (type === 'outgoing' && outgoingRingtoneRef.current) {
        outgoingRingtoneRef.current.currentTime = 0;
        outgoingRingtoneRef.current.volume = 0.3;
        outgoingRingtoneRef.current.play().catch(e => console.log('[Call] Ringtone play error:', e));
      }
    } catch (e) {
      console.error('[Call] Ringtone error:', e);
    }
  };

  const stopAllRingtones = () => {
    try {
      if (incomingRingtoneRef.current) {
        incomingRingtoneRef.current.pause();
        incomingRingtoneRef.current.currentTime = 0;
      }
      if (outgoingRingtoneRef.current) {
        outgoingRingtoneRef.current.pause();
        outgoingRingtoneRef.current.currentTime = 0;
      }
    } catch (e) {}
  };

  const playEndSound = () => {
    try {
      const audio = new Audio(SOUND_CALL_END);
      audio.volume = 0.3;
      audio.play().catch(() => {});
    } catch (e) {}
  };

  // Call handlers
  const handleIncomingCall = (callData) => {
    console.log('[Call] Incoming call from:', callData?.caller_name, callData);
    
    if (callStatus !== 'idle') {
      console.log('[Call] Already in a call, sending busy signal');
      // Send busy signal
      wsRef.current?.send(JSON.stringify({
        type: 'call_busy',
        target: callData.caller,
        call_id: callData.call_id
      }));
      return;
    }
    
    // Update ref immediately for WebSocket handlers
    incomingCallRef.current = callData;
    setIncomingCall(callData);
    setCallStatus('ringing');
    playRingtone('incoming');
    
    // Auto-reject after 60 seconds
    if (callTimeoutRef.current) clearTimeout(callTimeoutRef.current);
    callTimeoutRef.current = setTimeout(() => {
      console.log('[Call] Auto-rejecting unanswered call');
      rejectCall();
      toast.info('Missed call');
    }, 60000);
  };

  const handleCallAccepted = async (data) => {
    console.log('[Call] Call accepted, data:', data);
    stopAllRingtones();
    if (callTimeoutRef.current) clearTimeout(callTimeoutRef.current);
    setCallStatus('connecting');
    toast.success('Call accepted!');
    
    // Use the ref to get the current activeCall, fallback to data from server
    let currentCall = activeCallRef.current;
    
    // If we don't have the call data in ref, try to reconstruct from server response
    if (!currentCall && data.data) {
      currentCall = {
        ...data.data,
        recipient: data.data.recipient
      };
      activeCallRef.current = currentCall;
      setActiveCall(currentCall);
    }
    
    if (!currentCall) {
      console.error('[Call] No active call found when creating offer');
      toast.error('Call state lost. Please try again.');
      resetCallState();
      return;
    }
    
    // Create peer connection and send offer
    await createPeerConnection(true, currentCall.recipient, currentCall.call_id, currentCall.call_type);
  };

  const handleCallRejected = () => {
    console.log('[Call] Call rejected');
    stopAllRingtones();
    if (callTimeoutRef.current) clearTimeout(callTimeoutRef.current);
    playEndSound();
    resetCallState();
    toast.error('Call rejected');
  };

  const handleCallBusy = () => {
    console.log('[Call] User is busy');
    stopAllRingtones();
    if (callTimeoutRef.current) clearTimeout(callTimeoutRef.current);
    playEndSound();
    resetCallState();
    toast.error('User is busy');
  };

  const handleCallEnded = (data) => {
    console.log('[Call] Call ended:', data?.reason);
    stopAllRingtones();
    if (callTimeoutRef.current) clearTimeout(callTimeoutRef.current);
    playEndSound();
    cleanupMedia();
    resetCallState();
    toast.info(data?.reason === 'disconnected' ? 'Call disconnected' : 'Call ended');
  };

  const handleOffer = async (data) => {
    console.log('[Call] Received offer from:', data.from, data);
    try {
      // Create peer connection if not exists
      if (!peerConnectionRef.current) {
        const currentIncoming = incomingCallRef.current;
        await createPeerConnection(false, data.from, data.call_id, data.call_type || currentIncoming?.call_type || 'audio');
      }
      
      // Set remote description
      console.log('[Call] Setting remote description (offer)');
      await peerConnectionRef.current.setRemoteDescription(new RTCSessionDescription(data.sdp));
      
      // Process any pending ICE candidates
      console.log('[Call] Processing', pendingIceCandidatesRef.current.length, 'pending ICE candidates');
      for (const candidate of pendingIceCandidatesRef.current) {
        try {
          await peerConnectionRef.current.addIceCandidate(new RTCIceCandidate(candidate));
        } catch (e) {
          console.warn('[Call] Error adding pending ICE candidate:', e);
        }
      }
      pendingIceCandidatesRef.current = [];
      
      // Create and send answer
      const answer = await peerConnectionRef.current.createAnswer();
      await peerConnectionRef.current.setLocalDescription(answer);
      
      console.log('[Call] Sending answer to:', data.from);
      wsRef.current?.send(JSON.stringify({
        type: 'answer',
        target: data.from,
        sdp: answer,
        call_id: data.call_id
      }));
      
    } catch (e) {
      console.error('[Call] Error handling offer:', e);
      toast.error('Failed to connect call');
    }
  };

  const handleAnswer = async (data) => {
    console.log('[Call] Received answer from:', data.from, data);
    try {
      if (peerConnectionRef.current && peerConnectionRef.current.signalingState !== 'stable') {
        console.log('[Call] Setting remote description (answer)');
        await peerConnectionRef.current.setRemoteDescription(new RTCSessionDescription(data.sdp));
        
        // Process any pending ICE candidates
        console.log('[Call] Processing', pendingIceCandidatesRef.current.length, 'pending ICE candidates after answer');
        for (const candidate of pendingIceCandidatesRef.current) {
          try {
            await peerConnectionRef.current.addIceCandidate(new RTCIceCandidate(candidate));
          } catch (e) {
            console.warn('[Call] Error adding pending ICE candidate:', e);
          }
        }
        pendingIceCandidatesRef.current = [];
      } else {
        console.warn('[Call] Cannot set answer - PC not ready or already stable');
      }
    } catch (e) {
      console.error('[Call] Error handling answer:', e);
    }
  };

  const handleIceCandidate = async (data) => {
    try {
      if (peerConnectionRef.current && peerConnectionRef.current.remoteDescription) {
        console.log('[Call] Adding ICE candidate from:', data.from);
        await peerConnectionRef.current.addIceCandidate(new RTCIceCandidate(data.candidate));
      } else {
        // Queue the candidate for later
        console.log('[Call] Queueing ICE candidate - remote description not set yet');
        pendingIceCandidatesRef.current.push(data.candidate);
      }
    } catch (e) {
      console.error('[Call] Error adding ICE candidate:', e);
    }
  };

  // Reset call state
  const resetCallState = () => {
    setCallStatus('idle');
    setActiveCall(null);
    setIncomingCall(null);
    setCallDuration(0);
    setIsMuted(false);
    setIsVideoOff(false);
    setIsSpeakerOff(false);
    pendingIceCandidatesRef.current = [];
    // Clear the refs too
    activeCallRef.current = null;
    incomingCallRef.current = null;
  };

  // Cleanup media
  const cleanupMedia = () => {
    console.log('[Call] Cleaning up media');
    
    // Stop local stream tracks
    if (localStreamRef.current) {
      localStreamRef.current.getTracks().forEach(track => {
        track.stop();
        console.log('[Call] Stopped track:', track.kind);
      });
      localStreamRef.current = null;
    }
    
    // Close peer connection
    if (peerConnectionRef.current) {
      peerConnectionRef.current.close();
      peerConnectionRef.current = null;
    }
    
    // Clear video/audio elements
    if (localVideoRef.current) localVideoRef.current.srcObject = null;
    if (remoteVideoRef.current) remoteVideoRef.current.srcObject = null;
    if (remoteAudioRef.current) remoteAudioRef.current.srcObject = null;
  };

  // Create peer connection - explicitly pass params to avoid stale state
  const createPeerConnection = async (isInitiator, targetUsername, callId, callType = 'audio') => {
    console.log('[Call] Creating peer connection:', { isInitiator, targetUsername, callId, callType });
    
    // Close existing connection
    if (peerConnectionRef.current) {
      console.log('[Call] Closing existing peer connection');
      peerConnectionRef.current.close();
      peerConnectionRef.current = null;
    }
    
    // Clear pending ICE candidates
    pendingIceCandidatesRef.current = [];
    
    const pc = new RTCPeerConnection(ICE_SERVERS);
    peerConnectionRef.current = pc;
    
    console.log('[Call] RTCPeerConnection created');

    // Get local media
    try {
      const constraints = {
        audio: {
          echoCancellation: true,
          noiseSuppression: true,
          autoGainControl: true
        },
        video: callType === 'video'
      };
      
      console.log('[Call] Getting user media with constraints:', constraints);
      const stream = await navigator.mediaDevices.getUserMedia(constraints);
      localStreamRef.current = stream;
      
      // Add tracks to peer connection
      stream.getTracks().forEach(track => {
        console.log('[Call] Adding local track:', track.kind);
        pc.addTrack(track, stream);
      });

      // Show local video
      if (localVideoRef.current && callType === 'video') {
        localVideoRef.current.srcObject = stream;
      }
    } catch (err) {
      console.error('[Call] Error accessing media:', err);
      toast.error('Could not access camera/microphone: ' + err.message);
      cleanupMedia();
      resetCallState();
      return;
    }

    // Handle remote stream
    pc.ontrack = (event) => {
      console.log('[Call] Received remote track:', event.track.kind, event.streams);
      
      if (event.streams && event.streams[0]) {
        const remoteStream = event.streams[0];
        
        // Set video element
        if (remoteVideoRef.current) {
          console.log('[Call] Setting remote video stream');
          remoteVideoRef.current.srcObject = remoteStream;
          remoteVideoRef.current.play().catch(e => console.log('[Call] Video play error:', e));
        }
        
        // IMPORTANT: Set audio element for reliable audio playback
        if (remoteAudioRef.current) {
          console.log('[Call] Setting remote audio stream');
          remoteAudioRef.current.srcObject = remoteStream;
          remoteAudioRef.current.play().catch(e => console.log('[Call] Audio play error:', e));
        }
      }
      
      setCallStatus('connected');
      stopAllRingtones();
    };

    // Handle ICE candidates - use explicit target
    pc.onicecandidate = (event) => {
      if (event.candidate) {
        console.log('[Call] Sending ICE candidate to:', targetUsername);
        wsRef.current?.send(JSON.stringify({
          type: 'ice-candidate',
          target: targetUsername,
          candidate: event.candidate,
          call_id: callId
        }));
      }
    };
    
    // ICE gathering state
    pc.onicegatheringstatechange = () => {
      console.log('[Call] ICE gathering state:', pc.iceGatheringState);
    };

    // Connection state changes
    pc.onconnectionstatechange = () => {
      console.log('[Call] Connection state:', pc.connectionState);
      if (pc.connectionState === 'connected') {
        setCallStatus('connected');
        stopAllRingtones();
      } else if (pc.connectionState === 'failed') {
        console.log('[Call] Connection failed');
        handleCallEnded({ reason: 'disconnected' });
      } else if (pc.connectionState === 'disconnected') {
        // Disconnected can be temporary, wait a bit before ending
        console.log('[Call] Connection disconnected, waiting for reconnect...');
        setTimeout(() => {
          if (peerConnectionRef.current?.connectionState === 'disconnected') {
            console.log('[Call] Still disconnected after timeout');
            handleCallEnded({ reason: 'disconnected' });
          }
        }, 5000);
      }
    };

    pc.oniceconnectionstatechange = () => {
      console.log('[Call] ICE connection state:', pc.iceConnectionState);
      if (pc.iceConnectionState === 'connected' || pc.iceConnectionState === 'completed') {
        setCallStatus('connected');
        stopAllRingtones();
      } else if (pc.iceConnectionState === 'failed') {
        console.log('[Call] ICE connection failed');
        // Try ICE restart first
        if (isInitiator && pc.restartIce) {
          console.log('[Call] Attempting ICE restart');
          pc.restartIce();
        } else {
          // Wait before ending call
          setTimeout(() => {
            if (peerConnectionRef.current?.iceConnectionState === 'failed') {
              console.log('[Call] ICE still failed after restart attempt');
              handleCallEnded({ reason: 'connection_failed' });
            }
          }, 3000);
        }
      } else if (pc.iceConnectionState === 'disconnected') {
        console.log('[Call] ICE disconnected, waiting for reconnection...');
        // ICE disconnected is often temporary, wait before taking action
      }
    };
    
    pc.onsignalingstatechange = () => {
      console.log('[Call] Signaling state:', pc.signalingState);
    };

    // Create and send offer if initiator
    if (isInitiator) {
      try {
        console.log('[Call] Creating offer...');
        const offer = await pc.createOffer({
          offerToReceiveAudio: true,
          offerToReceiveVideo: callType === 'video'
        });
        
        console.log('[Call] Setting local description (offer)');
        await pc.setLocalDescription(offer);
        
        console.log('[Call] Sending offer to:', targetUsername);
        wsRef.current?.send(JSON.stringify({
          type: 'offer',
          target: targetUsername,
          sdp: offer,
          call_id: callId,
          call_type: callType
        }));
      } catch (e) {
        console.error('[Call] Error creating offer:', e);
        toast.error('Failed to create call offer');
      }
    }
    
    return pc;
  };

  // Initiate a call
  const initiateCall = async (recipientUsername, callType = 'audio') => {
    console.log('[Call] Initiating call to:', recipientUsername, 'type:', callType);
    
    if (callStatus !== 'idle') {
      toast.error('Already in a call');
      return;
    }
    
    if (!wsConnected) {
      toast.error('Not connected. Please try again.');
      return;
    }
    
    try {
      const headers = getAuthHeaders();
      if (!headers) {
        toast.error('Not authenticated');
        return;
      }

      const response = await axios.post(`${BACKEND_URL}/api/calling/initiate`, {
        recipient_username: recipientUsername,
        call_type: callType
      }, { headers });

      console.log('[Call] Call initiated:', response.data);

      const callData = {
        ...response.data.call_data,
        recipient: recipientUsername
      };
      // Update ref immediately for WebSocket handlers
      activeCallRef.current = callData;
      setActiveCall(callData);
      setCallStatus('outgoing');
      playRingtone('outgoing');
      toast.info(`Calling ${recipientUsername}...`);
      
      // Timeout after 60 seconds
      if (callTimeoutRef.current) clearTimeout(callTimeoutRef.current);
      callTimeoutRef.current = setTimeout(() => {
        console.log('[Call] Call timeout - no answer');
        endCall();
        toast.info('No answer');
      }, 60000);
    } catch (error) {
      console.error('[Call] Error initiating call:', error);
      toast.error(error.response?.data?.detail || 'Failed to initiate call');
    }
  };

  // Accept incoming call
  const acceptCall = async () => {
    if (!incomingCall) return;
    
    console.log('[Call] Accepting call:', incomingCall.call_id);
    
    try {
      stopAllRingtones();
      if (callTimeoutRef.current) clearTimeout(callTimeoutRef.current);
      
      const headers = getAuthHeaders();
      await axios.post(`${BACKEND_URL}/api/calling/respond/${incomingCall.call_id}`, {
        action: 'accept'
      }, { headers });

      // Move incoming call to active call
      const callData = {
        ...incomingCall,
        recipient: currentUser?.username // In accepted calls, current user is the recipient
      };
      // Update ref immediately for WebSocket handlers
      activeCallRef.current = callData;
      setActiveCall(callData);
      setIncomingCall(null);
      incomingCallRef.current = null;
      setCallStatus('connecting');
      
      // Don't create peer connection here - wait for offer to arrive
      // The offer handler will create the peer connection
      console.log('[Call] Call accepted, waiting for offer from caller');
    } catch (error) {
      console.error('[Call] Error accepting call:', error);
      toast.error('Failed to accept call');
      resetCallState();
    }
  };

  // Reject incoming call
  const rejectCall = async () => {
    if (!incomingCall) return;
    
    console.log('[Call] Rejecting call:', incomingCall.call_id);
    
    try {
      stopAllRingtones();
      if (callTimeoutRef.current) clearTimeout(callTimeoutRef.current);
      
      const headers = getAuthHeaders();
      await axios.post(`${BACKEND_URL}/api/calling/respond/${incomingCall.call_id}`, {
        action: 'reject'
      }, { headers });

      resetCallState();
    } catch (error) {
      console.error('[Call] Error rejecting call:', error);
      resetCallState();
    }
  };

  // End active call
  const endCall = async () => {
    console.log('[Call] Ending call');
    
    stopAllRingtones();
    if (callTimeoutRef.current) clearTimeout(callTimeoutRef.current);
    
    const callId = activeCall?.call_id || incomingCall?.call_id;
    
    // Cleanup first
    cleanupMedia();
    resetCallState();
    
    // Notify backend
    if (callId) {
      try {
        const headers = getAuthHeaders();
        await axios.post(`${BACKEND_URL}/api/calling/end/${callId}`, {}, { headers });
      } catch (error) {
        console.error('[Call] Error ending call on server:', error);
      }
    }
    
    playEndSound();
  };

  // Toggle mute
  const toggleMute = () => {
    console.log('[Call] Toggle mute, current:', isMuted);
    
    if (localStreamRef.current) {
      const audioTracks = localStreamRef.current.getAudioTracks();
      console.log('[Call] Audio tracks:', audioTracks.length);
      
      if (audioTracks.length > 0) {
        const audioTrack = audioTracks[0];
        audioTrack.enabled = !audioTrack.enabled;
        setIsMuted(!audioTrack.enabled);
        console.log('[Call] Audio track enabled:', audioTrack.enabled);
        toast.info(audioTrack.enabled ? 'Unmuted' : 'Muted');
      } else {
        console.warn('[Call] No audio tracks found');
        toast.error('No microphone available');
      }
    } else {
      console.warn('[Call] No local stream');
      toast.error('No audio stream');
    }
  };

  // Toggle video
  const toggleVideo = () => {
    console.log('[Call] Toggle video, current:', isVideoOff);
    
    if (localStreamRef.current) {
      const videoTracks = localStreamRef.current.getVideoTracks();
      
      if (videoTracks.length > 0) {
        const videoTrack = videoTracks[0];
        videoTrack.enabled = !videoTrack.enabled;
        setIsVideoOff(!videoTrack.enabled);
        toast.info(videoTrack.enabled ? 'Camera on' : 'Camera off');
      } else {
        toast.error('No camera available');
      }
    }
  };

  // Toggle speaker (mute remote audio)
  const toggleSpeaker = () => {
    console.log('[Call] Toggle speaker, current:', isSpeakerOff);
    
    const newMuted = !isSpeakerOff;
    
    if (remoteAudioRef.current) {
      remoteAudioRef.current.muted = newMuted;
    }
    if (remoteVideoRef.current) {
      remoteVideoRef.current.muted = newMuted;
    }
    
    setIsSpeakerOff(newMuted);
    toast.info(newMuted ? 'Speaker off' : 'Speaker on');
  };

  // Screen Share
  const toggleScreenShare = async () => {
    if (!peerConnectionRef.current || !localStreamRef.current) return;
    try {
      if (isScreenSharing) {
        // Switch back to camera
        const camStream = await navigator.mediaDevices.getUserMedia({ video: true, audio: false });
        const sender = peerConnectionRef.current.getSenders().find(s => s.track?.kind === 'video');
        if (sender) await sender.replaceTrack(camStream.getVideoTracks()[0]);
        if (localVideoRef.current) localVideoRef.current.srcObject = new MediaStream([...localStreamRef.current.getAudioTracks(), ...camStream.getVideoTracks()]);
        setIsScreenSharing(false);
        toast.info('Screen share stopped');
      } else {
        const screenStream = await navigator.mediaDevices.getDisplayMedia({ video: true });
        const sender = peerConnectionRef.current.getSenders().find(s => s.track?.kind === 'video');
        if (sender) await sender.replaceTrack(screenStream.getVideoTracks()[0]);
        if (localVideoRef.current) localVideoRef.current.srcObject = new MediaStream([...localStreamRef.current.getAudioTracks(), ...screenStream.getVideoTracks()]);
        screenStream.getVideoTracks()[0].onended = () => toggleScreenShare();
        setIsScreenSharing(true);
        toast.success('Screen sharing started');
      }
    } catch (e) {
      console.error('Screen share error:', e);
      toast.error('Screen share failed');
    }
  };

  // Hold / Resume
  const toggleHold = () => {
    if (!localStreamRef.current) return;
    const tracks = localStreamRef.current.getTracks();
    tracks.forEach(t => { t.enabled = isOnHold; });
    setIsOnHold(!isOnHold);
    toast.info(isOnHold ? 'Call resumed' : 'Call on hold');
  };

  // Save call notes
  const saveCallNotes = async () => {
    if (!activeCall || !callNotes.trim()) return;
    try {
      const headers = getAuthHeaders();
      if (!headers) return;
      await axios.post(`${BACKEND_URL}/api/calling/notes`, {
        call_id: activeCall.call_id,
        note: callNotes,
        recipient_username: activeCall.caller === currentUser?.username ? activeCall.recipient : activeCall.caller
      }, { headers });
      toast.success('Notes saved');
    } catch {}
  };

  // Fetch call history
  const fetchCallHistory = async () => {
    try {
      const headers = getAuthHeaders();
      if (!headers) return;
      const res = await axios.get(`${BACKEND_URL}/api/calling/history`, { headers });
      setCallHistory(res.data || []);
    } catch {}
  };

  // Format duration
  const formatDuration = (seconds) => {
    const mins = Math.floor(seconds / 60);
    const secs = seconds % 60;
    return `${mins.toString().padStart(2, '0')}:${secs.toString().padStart(2, '0')}`;
  };

  const value = {
    incomingCall,
    activeCall,
    callStatus,
    isMuted,
    isVideoOff,
    isSpeakerOff,
    isScreenSharing,
    isOnHold,
    callDuration,
    wsConnected,
    callNotes,
    showCallNotes,
    callHistory,
    localVideoRef,
    remoteVideoRef,
    remoteAudioRef,
    initiateCall,
    acceptCall,
    rejectCall,
    endCall,
    toggleMute,
    toggleVideo,
    toggleSpeaker,
    toggleScreenShare,
    toggleHold,
    setCallNotes,
    setShowCallNotes,
    saveCallNotes,
    fetchCallHistory,
    formatDuration
  };

  return (
    <CallContext.Provider value={value}>
      {children}
      {/* Hidden audio elements */}
      <audio ref={incomingRingtoneRef} loop preload="auto" src={RINGTONE_INCOMING} />
      <audio ref={outgoingRingtoneRef} loop preload="auto" src={RINGTONE_OUTGOING} />
      <audio ref={remoteAudioRef} autoPlay playsInline />
    </CallContext.Provider>
  );
};

// Call UI Component
export const CallUI = () => {
  const callContext = useContext(CallContext);
  
  if (!callContext) return null;

  const {
    incomingCall,
    activeCall,
    callStatus,
    isMuted,
    isVideoOff,
    isSpeakerOff,
    isScreenSharing,
    isOnHold,
    callDuration,
    callNotes,
    showCallNotes,
    localVideoRef,
    remoteVideoRef,
    acceptCall,
    rejectCall,
    endCall,
    toggleMute,
    toggleVideo,
    toggleSpeaker,
    toggleScreenShare,
    toggleHold,
    setCallNotes,
    setShowCallNotes,
    saveCallNotes,
    formatDuration
  } = callContext;

  // Don't render if idle
  if (callStatus === 'idle') return null;

  const isVideoCall = activeCall?.call_type === 'video' || incomingCall?.call_type === 'video';
  
  // Determine who the other party is based on call status
  let otherParty = 'Unknown';
  if (callStatus === 'outgoing' && activeCall) {
    // When calling someone, show their name (recipient)
    otherParty = activeCall.recipient_name || activeCall.recipient || 'Unknown';
  } else if (callStatus === 'ringing' && incomingCall) {
    // When receiving a call, show caller's name
    otherParty = incomingCall.caller_name || incomingCall.caller || 'Unknown';
  } else if (activeCall) {
    // During connected call, show the other party
    otherParty = activeCall.recipient_name || activeCall.caller_name || 'Unknown';
  } else if (incomingCall) {
    otherParty = incomingCall.caller_name || 'Unknown';
  }

  // Incoming call UI
  if (callStatus === 'ringing' && incomingCall) {
    return (
      <div className="fixed inset-0 z-[100] flex items-center justify-center bg-black/80 backdrop-blur-sm" data-testid="incoming-call-modal">
        <div className="bg-gradient-to-br from-gray-900 to-black border border-green-500/30 rounded-2xl p-8 w-[400px] text-center">
          <div className="w-24 h-24 rounded-full bg-gradient-to-br from-green-500/20 to-emerald-500/20 mx-auto mb-6 flex items-center justify-center animate-pulse">
            <PhoneIncoming className="w-12 h-12 text-green-400 animate-bounce" />
          </div>
          <h2 className="text-2xl font-bold text-white mb-2">
            Incoming {incomingCall.call_type === 'video' ? 'Video' : 'Voice'} Call
          </h2>
          <p className="text-xl text-cyan-400 mb-6">{incomingCall.caller_name}</p>
          <div className="flex justify-center gap-6">
            <Button
              onClick={rejectCall}
              className="w-16 h-16 rounded-full bg-red-500 hover:bg-red-600 text-white"
              data-testid="reject-call-btn"
            >
              <PhoneOff className="w-8 h-8" />
            </Button>
            <Button
              onClick={acceptCall}
              className="w-16 h-16 rounded-full bg-green-500 hover:bg-green-600 text-white animate-pulse"
              data-testid="accept-call-btn"
            >
              {isVideoCall ? <Video className="w-8 h-8" /> : <Phone className="w-8 h-8" />}
            </Button>
          </div>
        </div>
      </div>
    );
  }

  // Outgoing call UI
  if (callStatus === 'outgoing') {
    return (
      <div className="fixed inset-0 z-[100] flex items-center justify-center bg-black/90" data-testid="outgoing-call-modal">
        <div className="text-center">
          <div className="w-32 h-32 rounded-full bg-gradient-to-br from-cyan-500/20 to-purple-500/20 mx-auto mb-6 flex items-center justify-center border-2 border-cyan-500/30">
            <span className="text-5xl font-bold text-white">{otherParty.charAt(0).toUpperCase()}</span>
          </div>
          <h2 className="text-2xl font-bold text-white mb-2">Calling...</h2>
          <p className="text-xl text-cyan-400 mb-2">{otherParty}</p>
          <p className="text-gray-500 mb-8">Waiting for answer...</p>
          
          <div className="flex justify-center mb-8">
            <div className="relative">
              <div className="w-4 h-4 rounded-full bg-cyan-500 animate-ping absolute" />
              <div className="w-4 h-4 rounded-full bg-cyan-500" />
            </div>
          </div>
          
          <Button
            onClick={endCall}
            className="w-16 h-16 rounded-full bg-red-500 hover:bg-red-600 text-white"
            data-testid="cancel-call-btn"
          >
            <PhoneOff className="w-8 h-8" />
          </Button>
        </div>
      </div>
    );
  }

  // Active call UI (connecting or connected)
  if (callStatus === 'connecting' || callStatus === 'connected') {
    return (
      <div className="fixed inset-0 z-[100] bg-gray-950" data-testid="active-call-modal">
        <div className="relative h-full">
          {isVideoCall ? (
            <>
              {/* Remote video - full screen */}
              <video
                ref={remoteVideoRef}
                autoPlay
                playsInline
                className="w-full h-full object-cover bg-gray-900"
              />
              {/* Local video - PIP */}
              <div className="absolute top-4 right-4 w-48 h-36 rounded-xl overflow-hidden border-2 border-cyan-500/50 shadow-lg bg-gray-800">
                <video
                  ref={localVideoRef}
                  autoPlay
                  playsInline
                  muted
                  className="w-full h-full object-cover"
                />
              </div>
            </>
          ) : (
            /* Audio call UI */
            <div className="h-full flex flex-col items-center justify-center bg-gradient-to-br from-gray-900 via-gray-950 to-black">
              <div className="w-32 h-32 rounded-full bg-gradient-to-br from-cyan-500/20 to-purple-500/20 flex items-center justify-center mb-6 border-2 border-cyan-500/30">
                <span className="text-5xl font-bold text-white">{otherParty.charAt(0).toUpperCase()}</span>
              </div>
              <h2 className="text-2xl font-bold text-white mb-2">{otherParty}</h2>
              <p className="text-gray-400 text-xl mb-4" data-testid="call-duration">
                {callStatus === 'connecting' ? 'Connecting...' : formatDuration(callDuration)}
              </p>
              {/* Audio visualizer */}
              {callStatus === 'connected' && (
                <div className="flex items-end gap-1 h-8 mb-4">
                  {[1,2,3,4,5].map(i => (
                    <div
                      key={i}
                      className="w-1.5 bg-cyan-500 rounded-full animate-pulse"
                      style={{
                        height: `${8 + Math.random() * 16}px`,
                        animationDelay: `${i * 0.1}s`
                      }}
                    />
                  ))}
                </div>
              )}
            </div>
          )}

          {/* Call info overlay */}
          <div className="absolute top-4 left-4 bg-black/60 backdrop-blur-sm rounded-xl px-4 py-2">
            <p className="text-white font-medium">{otherParty}</p>
            <p className="text-lg text-cyan-400 font-mono" data-testid="call-timer">
              {callStatus === 'connecting' ? 'Connecting...' : formatDuration(callDuration)}
            </p>
          </div>

          {/* Call controls */}
          <div className="absolute bottom-8 left-1/2 transform -translate-x-1/2">
            <div className="flex items-center gap-4 bg-black/60 backdrop-blur-sm rounded-full px-6 py-4">
              {/* Mute button */}
              <Button
                onClick={toggleMute}
                className={`w-14 h-14 rounded-full transition-colors ${
                  isMuted ? 'bg-red-500 hover:bg-red-600' : 'bg-gray-700 hover:bg-gray-600'
                }`}
                data-testid="mute-btn"
                title={isMuted ? 'Unmute' : 'Mute'}
              >
                {isMuted ? <MicOff className="w-6 h-6" /> : <Mic className="w-6 h-6" />}
              </Button>
              
              {/* Video toggle */}
              {isVideoCall && (
                <Button
                  onClick={toggleVideo}
                  className={`w-14 h-14 rounded-full transition-colors ${
                    isVideoOff ? 'bg-red-500 hover:bg-red-600' : 'bg-gray-700 hover:bg-gray-600'
                  }`}
                  data-testid="video-toggle-btn"
                  title={isVideoOff ? 'Turn on camera' : 'Turn off camera'}
                >
                  {isVideoOff ? <VideoOff className="w-6 h-6" /> : <Video className="w-6 h-6" />}
                </Button>
              )}

              {/* Speaker toggle */}
              <Button
                onClick={toggleSpeaker}
                className={`w-14 h-14 rounded-full transition-colors ${
                  isSpeakerOff ? 'bg-red-500 hover:bg-red-600' : 'bg-gray-700 hover:bg-gray-600'
                }`}
                data-testid="speaker-btn"
                title={isSpeakerOff ? 'Turn on speaker' : 'Turn off speaker'}
              >
                {isSpeakerOff ? <VolumeX className="w-6 h-6" /> : <Volume2 className="w-6 h-6" />}
              </Button>

              {/* Screen Share */}
              {isVideoCall && (
                <Button
                  onClick={toggleScreenShare}
                  className={`w-14 h-14 rounded-full transition-colors ${
                    isScreenSharing ? 'bg-cyan-500 hover:bg-cyan-600' : 'bg-gray-700 hover:bg-gray-600'
                  }`}
                  data-testid="screen-share-call-btn"
                  title={isScreenSharing ? 'Stop sharing' : 'Share screen'}
                >
                  <Monitor className="w-6 h-6" />
                </Button>
              )}

              {/* Hold */}
              <Button
                onClick={toggleHold}
                className={`w-14 h-14 rounded-full transition-colors ${
                  isOnHold ? 'bg-amber-500 hover:bg-amber-600' : 'bg-gray-700 hover:bg-gray-600'
                }`}
                data-testid="hold-btn"
                title={isOnHold ? 'Resume' : 'Hold'}
              >
                {isOnHold ? <Play className="w-6 h-6" /> : <Pause className="w-6 h-6" />}
              </Button>

              {/* Notes */}
              <Button
                onClick={() => setShowCallNotes(!showCallNotes)}
                className={`w-14 h-14 rounded-full transition-colors ${
                  showCallNotes ? 'bg-purple-500 hover:bg-purple-600' : 'bg-gray-700 hover:bg-gray-600'
                }`}
                data-testid="call-notes-btn"
                title="Call notes"
              >
                <FileText className="w-6 h-6" />
              </Button>

              {/* End call */}
              <Button
                onClick={endCall}
                className="w-14 h-14 rounded-full bg-red-500 hover:bg-red-600"
                data-testid="end-call-btn"
                title="End call"
              >
                <PhoneOff className="w-6 h-6" />
              </Button>
            </div>
          </div>

          {/* Call Notes Panel */}
          {showCallNotes && (
            <div className="absolute top-4 right-4 w-72 bg-gray-900/95 border border-purple-500/30 rounded-2xl p-4 backdrop-blur-sm z-10" data-testid="call-notes-panel">
              <div className="flex items-center justify-between mb-3">
                <h3 className="text-white font-medium text-sm flex items-center gap-2">
                  <FileText className="w-4 h-4 text-purple-400" /> Call Notes
                </h3>
                <button onClick={() => setShowCallNotes(false)} className="text-gray-500 hover:text-white">
                  <X className="w-4 h-4" />
                </button>
              </div>
              <textarea value={callNotes} onChange={e => setCallNotes(e.target.value)}
                placeholder="Add notes about this call..."
                className="w-full h-32 bg-gray-800 border border-gray-700 rounded-xl p-3 text-white text-xs placeholder-gray-500 focus:outline-none focus:ring-1 focus:ring-purple-500/30 resize-none mb-3" />
              <Button onClick={saveCallNotes} size="sm" className="w-full bg-purple-500 hover:bg-purple-600 text-xs">
                Save Notes
              </Button>
            </div>
          )}

          {/* Hold Indicator */}
          {isOnHold && (
            <div className="absolute top-4 left-1/2 -translate-x-1/2 bg-amber-500/20 border border-amber-500/40 rounded-xl px-4 py-2">
              <p className="text-amber-400 text-sm font-medium flex items-center gap-2"><Pause className="w-4 h-4" /> Call on Hold</p>
            </div>
          )}
        </div>
      </div>
    );
  }

  return null;
};

// Call Button Component
export const CallButton = ({ username, displayName, variant = 'icon' }) => {
  const callContext = useContext(CallContext);
  
  if (!callContext) return null;

  const { initiateCall, callStatus, wsConnected } = callContext;
  const isInCall = callStatus !== 'idle';
  const isDisabled = isInCall || !wsConnected;

  if (variant === 'full') {
    return (
      <div className="flex gap-2">
        <Button
          onClick={() => initiateCall(username, 'audio')}
          disabled={isDisabled}
          className="bg-green-500/20 hover:bg-green-500/30 text-green-400 border border-green-500/30 disabled:opacity-50"
          size="sm"
          data-testid="voice-call-btn"
        >
          <Phone className="w-4 h-4 mr-2" />
          Voice Call
        </Button>
        <Button
          onClick={() => initiateCall(username, 'video')}
          disabled={isDisabled}
          className="bg-cyan-500/20 hover:bg-cyan-500/30 text-cyan-400 border border-cyan-500/30 disabled:opacity-50"
          size="sm"
          data-testid="video-call-btn"
        >
          <Video className="w-4 h-4 mr-2" />
          Video Call
        </Button>
      </div>
    );
  }

  return (
    <div className="flex gap-1">
      <button
        onClick={() => initiateCall(username, 'audio')}
        disabled={isDisabled}
        className="p-2 rounded-lg bg-green-500/10 hover:bg-green-500/20 text-green-400 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
        title={wsConnected ? `Voice call ${displayName || username}` : 'Connecting...'}
        data-testid="voice-call-icon-btn"
      >
        <Phone className="w-4 h-4" />
      </button>
      <button
        onClick={() => initiateCall(username, 'video')}
        disabled={isDisabled}
        className="p-2 rounded-lg bg-cyan-500/10 hover:bg-cyan-500/20 text-cyan-400 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
        title={wsConnected ? `Video call ${displayName || username}` : 'Connecting...'}
        data-testid="video-call-icon-btn"
      >
        <Video className="w-4 h-4" />
      </button>
    </div>
  );
};

export default CallProvider;
